<template>
  <div class="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4">
    <div class="w-full max-w-md mx-4 p-5 sm:p-6 rounded-[20px] bg-white shadow-2xl relative">
      <!-- Close Button -->
      <button
        type="button"
        class="w-10 h-10 -mr-2 -mt-2 absolute top-4 right-4 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors cursor-pointer border-none bg-transparent"
        @click="$emit('close')"
      >
        <svg
          viewBox="0 0 14 14"
          fill="none"
          class="w-[12px] h-[12px] text-gray-500"
        >
          <path
            d="M1.5 1.5L12.5 12.5M1.5 12.5L12.5 1.5"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <h3 class="text-xl font-['Raleway'] font-bold text-[#043f42] mb-1">
        Save as Cohort Template
      </h3>
      <p class="text-xs font-['Raleway'] text-gray-500 mb-5">
        Create a reusable template from <strong>{{ cohort.name }}</strong> with all programme structures, field coordinates, and PDF documents.
      </p>

      <form @submit.prevent="submitSave">
        <div class="mb-4">
          <label class="block text-xs font-bold text-[#043f42] font-['Raleway'] mb-1">
            Template Name <span class="text-red-500">*</span>
          </label>
          <input
            v-model="templateName"
            type="text"
            required
            class="w-full h-11 px-3 border border-gray-300 rounded-[10px] focus:outline-none focus:border-[#01d680] focus:ring-2 focus:ring-[#01d680]/20 font-['Raleway'] text-sm"
          >
        </div>

        <div class="mb-4">
          <label class="block text-xs font-bold text-[#043f42] font-['Raleway'] mb-1">
            Description (Optional)
          </label>
          <textarea
            v-model="description"
            rows="3"
            placeholder="Notes about SETA qualification, intake requirements, etc."
            class="w-full p-3 border border-gray-300 rounded-[10px] focus:outline-none focus:border-[#01d680] focus:ring-2 focus:ring-[#01d680]/20 font-['Raleway'] text-xs resize-none"
          />
        </div>

        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-['Raleway']"
        >
          {{ error }}
        </div>

        <div class="flex items-center justify-end gap-3 mt-6">
          <button
            type="button"
            class="px-5 py-2.5 rounded-[38px] border border-gray-300 text-gray-700 font-['Raleway'] font-bold text-xs hover:bg-gray-50 transition-colors cursor-pointer bg-white"
            @click="$emit('close')"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="px-5 py-2.5 rounded-[38px] bg-[#01d680] text-[#043f42] font-['Raleway'] font-bold text-xs hover:bg-[#00c270] transition-colors cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 border-none"
          >
            <span
              v-if="isSubmitting"
              class="loading loading-spinner loading-xs"
            />
            {{ isSubmitting ? 'Saving...' : 'Save Template' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SaveAsCohortTemplateModal',
  props: {
    cohort: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'saved'],
  data () {
    return {
      templateName: `${this.cohort.name} Template`,
      description: '',
      isSubmitting: false,
      error: null
    }
  },
  methods: {
    async submitSave () {
      this.isSubmitting = true
      this.error = null

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

        const response = await fetch(`/cohorts/${this.cohort.id}/save_as_template`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json'
          },
          body: JSON.stringify({
            name: this.templateName,
            description: this.description
          })
        })

        const data = await response.json()

        if (!response.ok) {
          if (response.status === 403) {
            const gateModal = document.getElementById('subscription_gate_modal')
            if (gateModal) {
              this.$emit('close')
              gateModal.showModal()
              return
            }
          }
          throw new Error(data.error || 'Failed to save cohort template')
        }

        if (data.redirect_url) {
          window.location.href = data.redirect_url
        } else {
          this.$emit('saved', data)
          this.$emit('close')
        }
      } catch (err) {
        this.error = err.message
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>
